/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {
    EntityRelationsEditor,
    RelationStatus,
    getEntityRelationStatus,
    EntityRelationsEditorProps,
    EntityRelationsEditorState,
    getRelatedCount,
    EntityRelationsSpecification,
} from './EntityRelationsEditor';
import {
    lookupEntitiesByField,
    lookupEntitiesRelatedWithChanges,
} from './EntityRelationsEditorLookup';
import {TranslationContext} from './commons';

/**
 * The props for a relation editor for selecting a single related entity
 */
export interface SingleEntityRelationsEditorProps
    extends EntityRelationsEditorProps {
    /**
     * Lookups relatable entities using the specified input
     * @param specification The specification for the related entities
     */
    lookupRelatableEntities: (
        specification: EntityRelationsSpecification
    ) => Promise<providers.Entity[]>;
}

/**
 * The state for a relation editor for selecting a single related entity
 */
export interface SingleEntityRelationsEditorState
    extends EntityRelationsEditorState {
    /**
     * The currently selected entity
     */
    selected: providers.Entity | null;
}

/**
 * The default props for an editor of entity relations
 */
export const ENTITY_RELATIONS_EDITOR_SINGLE_PROPS = {
    className: '',
    lookupLimitRelatable: 5,
    lookupLimitRelated: -1,
    baseChanges: {
        added: [],
        removed: [],
    },
    lookupRelatableEntities: (specification: EntityRelationsSpecification) =>
        lookupEntitiesByField(specification, 'name', '', -1),
    renderLookup: (component: EntityRelationsEditor) => {
        // not used for a single selector
        return null;
    },
    renderRelatable: (
        component: EntityRelationsEditor,
        entity: providers.Entity,
        status: RelationStatus
    ) => {
        return (
            <option key={`relatable-${entity['@eid']}`} value={entity['@eid']}>
                {entity['name']}
            </option>
        );
    },
    renderContentRelatables: (component: SingleEntityRelationsEditor) => {
        return (
            <div
                style={{
                    width: '450px',
                    height: '75px',
                    overflowY: 'scroll',
                    overflowX: 'scroll',
                }}
            >
                <select
                    value={
                        component.state.selected === null
                            ? -1
                            : component.state.selected['@eid']
                    }
                    onChange={component.onSelect}
                >
                    <option value={-1}>
                        {component.context.translate('-- select --')}
                    </option>
                    {component.state.relatables.map((entity) =>
                        component.props.renderRelatable(
                            component,
                            entity,
                            getEntityRelationStatus(
                                component.props.specification,
                                component.props.baseChanges,
                                component.state.changes,
                                entity
                            )
                        )
                    )}
                </select>
            </div>
        );
    },
    renderContentLoading: (component: EntityRelationsEditor) => {
        return (
            <div
                style={{
                    width: '450px',
                    height: '75px',
                }}
            >
                {component.context.translate('loading')}
            </div>
        );
    },
    renderButtonValidate: (component: EntityRelationsEditor) => {
        let total =
            component.state.baseCount +
            component.state.changes.added.length -
            component.state.changes.removed.length;
        return (
            <button
                className="btn btn-primary"
                onClick={(e) =>
                    component.props.validate(component.state.changes)
                }
            >
                <span>
                    {component.context.translate('Validate') +
                        ` ${total} ${component.context.translate(
                            total >= 2 ? 'entities' : 'entity'
                        )}`}
                </span>
                {component.state.changes.added.length > 0 ||
                component.state.changes.removed.length > 0 ? (
                    <span>
                        {' ('}
                        {component.state.changes.added.length > 0 ? (
                            <span style={{color: 'green'}}>{`${
                                component.state.changes.added.length
                            } ${component.context.translate('added')}`}</span>
                        ) : null}
                        {component.state.changes.removed.length > 0 &&
                        component.state.changes.added.length > 0 ? (
                            <span>{', '}</span>
                        ) : null}
                        {component.state.changes.removed.length > 0 ? (
                            <span style={{color: 'red'}}>{`${
                                component.state.changes.removed.length
                            } ${component.context.translate('removed')}`}</span>
                        ) : null}
                        {')'}
                    </span>
                ) : null}
            </button>
        );
    },
    renderButtonCancel: (component: EntityRelationsEditor) => {
        return (
            <button
                className="btn btn-primary"
                onClick={(e) => component.props.cancel()}
            >
                <span>{component.context.translate('Cancel')}</span>
            </button>
        );
    },
    render: (component: EntityRelationsEditor) => {
        return (
            <div className={component.props.className}>
                <div>
                    {component.state.isLoadingRelatables
                        ? component.props.renderContentLoading(component)
                        : component.props.renderContentRelatables(component)}
                </div>
                <div>
                    {component.props.renderButtonCancel(component)}
                    {component.props.renderButtonValidate(component)}
                </div>
            </div>
        );
    },
};

/**
 * The editor of entity relations in the special case of
 * a cardinality of max 1 for the relatables
 */
export class SingleEntityRelationsEditor
    extends React.Component<
        SingleEntityRelationsEditorProps,
        SingleEntityRelationsEditorState
    >
    implements
        EntityRelationsEditor<
            SingleEntityRelationsEditorProps,
            SingleEntityRelationsEditorState
        >
{
    public static contextType = TranslationContext;
    public context!: React.ContextType<typeof TranslationContext>;
    public static defaultProps = ENTITY_RELATIONS_EDITOR_SINGLE_PROPS;
    state: SingleEntityRelationsEditorState;
    _isMounted: boolean;

    constructor(props: Readonly<SingleEntityRelationsEditorProps>) {
        super(props);
        this._isMounted = false;
        this.state = {
            baseCount: 0,
            changes: {added: [], removed: []},
            isLoadingRelatables: false,
            relatables: [],
            selected: null,
        };
        this.onSelect = this.onSelect.bind(this);
        this.onToggle = this.onToggle.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        let self = this;
        this.setState({isLoadingRelatables: true});
        this.props
            .lookupRelatableEntities(this.props.specification)
            .then((entities) => {
                self.onLookupResult(entities);
            })
            .catch((reason) => {
                console.log(reason);
            });
        getRelatedCount(this.props.specification)
            .then((count) => {
                let finalCount =
                    count +
                    self.props.baseChanges.added.length -
                    self.props.baseChanges.removed.length;
                if (self._isMounted) {
                    self.setState({baseCount: finalCount});
                }
            })
            .catch((reason) => {
                console.log(reason);
            });
        lookupEntitiesRelatedWithChanges(this.props.specification, -1, [
            this.props.baseChanges,
            this.state.changes,
        ])
            .then((entities) => {
                if (self._isMounted) {
                    self.setState({
                        selected: entities.length === 0 ? null : entities[0],
                    });
                }
            })
            .catch((reason) => {
                console.log(reason);
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    onLookupLoading() {
        this.setState({isLoadingRelatables: true});
    }

    onLookupResult(result: providers.Entity[]) {
        if (this._isMounted) {
            this.setState({isLoadingRelatables: false, relatables: result});
        }
    }

    onSelect(event: React.FormEvent<HTMLSelectElement>) {
        let eid = parseInt(event.currentTarget.value, 10);
        let changes = this.state.changes;
        if (this.state.selected !== null) {
            let toRemove: providers.Entity = this.state.selected;
            let index = changes.added.findIndex(
                (e) => e['@eid'] === toRemove['@eid']
            );
            if (index >= 0) {
                // was previously added
                changes = {
                    added: changes.added.filter((e, i) => i !== index),
                    removed: changes.removed,
                };
            } else {
                // remove
                changes = {
                    added: changes.added,
                    removed: changes.removed.concat(this.state.selected),
                };
            }
        }
        if (eid === -1) {
            this.setState({selected: null, changes: changes});
        } else {
            let newSelected = this.state.relatables.find(
                (e) => e['@eid'] === eid
            );
            if (newSelected === undefined) {
                this.setState({selected: null, changes: changes});
            } else {
                let index = changes.removed.findIndex((e) => e['@eid'] === eid);
                if (index >= 0) {
                    // was previously removed
                    changes = {
                        added: changes.added,
                        removed: changes.removed.filter((e, i) => i !== index),
                    };
                } else {
                    // add
                    changes = {
                        added: changes.added.concat(newSelected),
                        removed: changes.removed,
                    };
                }
                this.setState({selected: newSelected, changes: changes});
            }
        }
    }

    onToggle(entity: providers.Entity, isRelated: boolean) {
        // do nothing
    }

    render(): JSX.Element {
        return this.props.render(this);
    }
}
